/**
 * AskOrSearch — SHARED SOURCE OF TRUTH (Harmony-QMS plan 05-08).
 * COPY (plan 05-10) — source of truth is harmony-qms-fieldlink/src/components/AskOrSearch;
 * do NOT edit here, sync manually from the fieldlink repo.
 * SYNCED 2026-10-06 from fieldlink 6ebb421 (13.1-10 back-port): content matches upstream.
 *
 * One input box, two behaviours, zero mode switches: every submit fires BOTH
 * the Q&S Advisor chat (cited answer) and a library title-search (matching
 * documents). A question leads with the answer; a short doc-name-ish query
 * (≤3 words, no question mark) leads with the document matches.
 *
 * Plan 05-10 COPIES this folder into harmony-qms-dashboard with a different
 * transport. RULES for edits here:
 *  - no imports from outside this folder (react only)
 *  - transport ({ postChat, searchLibrary, openDoc }) injected via props
 *  - phone-first; no app-specific classes beyond this folder's own css
 */

import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  AskOrSearchProps,
  ChatMessage,
  ChatResponse,
  LibraryMatch,
} from './types';
import { AnswerMarkdown } from './markdown';
import './askorsearch.css';

const MATCH_LIMIT = 8;

/** Doc-name-ish queries lead with document matches, not the answer. */
function isShortQuery(q: string): boolean {
  return q.split(/\s+/).filter(Boolean).length <= 3 && !q.includes('?');
}

type AnswerState =
  | { status: 'idle' }
  | { status: 'thinking' }
  | { status: 'done'; response: ChatResponse }
  | { status: 'error'; message: string };

type MatchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; matches: LibraryMatch[] }
  | { status: 'error' };

const DOC_STATE_HINT: Record<string, string> = {
  'office-only': 'Not yet available on this surface — ask the office',
  unavailable: 'Not yet available on this surface — ask the office',
};

export function AskOrSearch({ postChat, searchLibrary, openDoc, placeholder }: AskOrSearchProps) {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [answer, setAnswer] = useState<AnswerState>({ status: 'idle' });
  const [matches, setMatches] = useState<MatchState>({ status: 'idle' });
  const [docsFirst, setDocsFirst] = useState(false);
  const submitSeq = useRef(0);

  const thinking = answer.status === 'thinking';

  function reset() {
    submitSeq.current += 1;
    setHistory([]);
    setAnswer({ status: 'idle' });
    setMatches({ status: 'idle' });
    setInput('');
    setDocsFirst(false);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if (!q || thinking) return;

    const seq = ++submitSeq.current;
    const conversation: ChatMessage[] = [...history, { role: 'user', content: q }];
    setDocsFirst(isShortQuery(q));
    setAnswer({ status: 'thinking' });
    setMatches({ status: 'loading' });
    setInput('');

    postChat(conversation)
      .then((response) => {
        if (seq !== submitSeq.current) return;
        setAnswer({ status: 'done', response });
        setHistory([...conversation, { role: 'assistant', content: response.content }]);
      })
      .catch(() => {
        if (seq !== submitSeq.current) return;
        setAnswer({
          status: 'error',
          message:
            "Sorry — the answer service couldn't respond just now. The matching documents below may still help, or try again in a moment.",
        });
        // Keep the failed turn out of history so a retry is clean.
      });

    searchLibrary(q)
      .then((found) => {
        if (seq !== submitSeq.current) return;
        setMatches({ status: 'done', matches: found.slice(0, MATCH_LIMIT) });
      })
      .catch(() => {
        if (seq !== submitSeq.current) return;
        setMatches({ status: 'error' });
      });
  }

  const answerBlock = (
    <section className="aos-answer" aria-live="polite">
      {answer.status === 'thinking' && (
        <div className="aos-card aos-card--thinking">
          <span className="aos-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          Checking Harmony&rsquo;s policies and processes…
        </div>
      )}
      {answer.status === 'error' && <div className="aos-card aos-card--error">{answer.message}</div>}
      {answer.status === 'done' && (
        <div className="aos-card">
          {answer.response.truncated && (
            <p className="aos-truncated">
              This answer was cut short — it may be incomplete. Try a more
              specific question for the full picture.
            </p>
          )}
          <AnswerMarkdown text={answer.response.content} />
          {answer.response.citations.length > 0 && (
            <div className="aos-cites">
              <span className="aos-cites__label">Sources</span>
              {answer.response.citations.map((c, i) =>
                c.docId ? (
                  <button
                    key={i}
                    type="button"
                    className="aos-chip aos-chip--open"
                    onClick={() => openDoc(c.docId!)}
                  >
                    <span aria-hidden="true">📄</span> {c.title}
                  </button>
                ) : c.fallback ? (
                  <span key={i} className="aos-chip-group">
                    <span className="aos-chip" title={c.hint ?? (c.docState ? DOC_STATE_HINT[c.docState] : undefined)}>
                      {c.title}
                    </span>
                    <a className="aos-fallback" href={c.fallback.url} target="_blank" rel="noopener noreferrer">
                      {c.fallback.label}
                    </a>
                  </span>
                ) : (
                  <span key={i} className="aos-chip" title={c.hint ?? (c.docState ? DOC_STATE_HINT[c.docState] : undefined)}>
                    {c.title}
                  </span>
                ),
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );

  const matchesBlock = (
    <section className="aos-matches">
      {matches.status === 'loading' && <p className="aos-matches__hint">Searching the library…</p>}
      {matches.status === 'done' && matches.matches.length > 0 && (
        <>
          <h2 className="aos-matches__title">Matching documents</h2>
          <ul className="aos-matches__list">
            {matches.matches.map((m) =>
              m.docState === 'available' ? (
                <li key={m.id}>
                  <button type="button" className="aos-match" onClick={() => openDoc(m.id)}>
                    <span className="aos-match__kind">{m.kind === 'process' ? 'Process' : 'Policy'}</span>
                    <span className="aos-match__text">
                      <span className="aos-match__doc-title">{m.title}</span>
                      {m.category && <span className="aos-match__meta">{m.category}</span>}
                    </span>
                    <span className="aos-match__open" aria-hidden="true">
                      ›
                    </span>
                  </button>
                </li>
              ) : (
                <li key={m.id}>
                  <div className="aos-match aos-match--closed">
                    <span className="aos-match__kind">{m.kind === 'process' ? 'Process' : 'Policy'}</span>
                    <span className="aos-match__text">
                      <span className="aos-match__doc-title">{m.title}</span>
                      <span className="aos-match__meta">
                        {m.hint ?? 'Not yet available on this surface — ask the office'}
                      </span>
                      {m.fallback && (
                        <a
                          className="aos-fallback"
                          href={m.fallback.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {m.fallback.label}
                        </a>
                      )}
                    </span>
                  </div>
                </li>
              ),
            )}
          </ul>
        </>
      )}
      {matches.status === 'done' && matches.matches.length === 0 && answer.status !== 'idle' && (
        <p className="aos-matches__hint">No document titles matched that search.</p>
      )}
    </section>
  );

  return (
    <div className="aos">
      <form className="aos-form" onSubmit={onSubmit}>
        <input
          type="text"
          className="aos-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder ?? 'Ask a question or search for a document…'}
          aria-label="Ask a question or search for a document"
          enterKeyHint="search"
        />
        <button type="submit" className="aos-submit" disabled={thinking || !input.trim()}>
          {thinking ? '…' : 'Ask'}
        </button>
      </form>

      {docsFirst ? (
        <>
          {matchesBlock}
          {answerBlock}
        </>
      ) : (
        <>
          {answerBlock}
          {matchesBlock}
        </>
      )}

      {history.length > 0 && answer.status === 'done' && (
        <div className="aos-footer">
          <span className="aos-footer__hint">You can ask a follow-up question above, or</span>
          <button type="button" className="aos-reset" onClick={reset}>
            start a new question
          </button>
        </div>
      )}
    </div>
  );
}
